// Task #20029 — DSv2 admin claims workspace client (api-server
// routes/admin-claims.ts). Types local to this module; only BadgeCounts
// lives in types.ts.
import { adminFetch } from "./_client";

export type AdminClaimStatus =
  | "draft"
  | "submitted"
  | "under_review"
  | "awaiting_info"
  | "sent_to_insurer"
  | "approved"
  | "paid"
  | "denied"
  | "closed"
  | "withdrawn"
  | "auto_resolved"
  | "abandoned";

export type AdminClaimAction =
  | "start_review"
  | "request_info"
  | "resume_review"
  | "send_to_insurer"
  | "approve"
  | "deny"
  | "mark_paid"
  | "close"
  | "withdraw";

export interface AdminClaimRow {
  id: number;
  trip_id: number;
  host_id: string;
  renter_id: string;
  status: string;
  claim_status: AdminClaimStatus;
  category: string | null;
  description: string | null;
  amount_cents: number | null;
  currency: string;
  insurance_source: string | null;
  opened_by: string | null;
  reference: string | null;
  assignee_id: number | null;
  insurer_ref: string | null;
  submitted_at: string | null;
  sent_to_insurer_at: string | null;
  resolution: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  trip_public_id: string | null;
  car: { year: number | null; make: string | null; model: string | null } | null;
  status_label: string;
  sla: { firstResponseDueAt: string | null; overdue: boolean };
}

export interface AdminClaimEvidence {
  id: number;
  kind: string;
  source: string;
  storage_key: string | null;
  file_url: string | null;
  filename: string | null;
  mime_type: string | null;
  note: string | null;
  uploaded_by: string | null;
  created_at: string;
}

export interface AdminClaimEvent {
  id: number;
  action: string;
  payload: Record<string, unknown> | null;
  performed_by: string | null;
  created_at: string;
}

// Task #20045-#20046 — workspace joins for the full-screen claim page.
export interface AdminWorkspacePhoto {
  id: number;
  url: string;
  slot: string | null;
  phase: "pre" | "post" | "damage" | null;
  note: string | null;
  created_at: string | null;
}

export interface AdminComparePair {
  slot: string;
  label: string;
  before: AdminWorkspacePhoto | null;
  after: AdminWorkspacePhoto | null;
}

export interface AdminInspectionPhaseFacts {
  id: number;
  phase: "pre" | "post";
  created_at: string | null;
  mileage: number | null;
  fuel_eighths: number | null;
  guest_signed: boolean;
  host_signed: boolean;
  renter_present: boolean | null;
  host_present: boolean | null;
  skip_reason: string | null;
  status: string | null;
  damage_markers: unknown;
  photo_count: number;
}

export interface AdminClaimDetail {
  claim: AdminClaimRow & { status_label: string };
  trip: {
    id: number;
    public_id: string | null;
    status: string | null;
    rental_type: string | null;
    started_at: string | null;
    drop_off: string | null;
    completed_at: string | null;
    car: { year: number | null; make: string | null; model: string | null } | null;
  };
  host: { id: string; name?: string; email?: string | null };
  renter: { id: string; name?: string; email?: string | null };
  assignee: { id: number; name: string } | null;
  evidence: AdminClaimEvidence[];
  events: AdminClaimEvent[];
  communications: AdminClaimCommunication[];
  deadline: {
    id: number;
    deadline_at: string;
    substantiated_at: string | null;
    resolution: string | null;
    resolved_at: string | null;
  } | null;
  tracker: { stages: Array<{ key: string; label: string; state: string }>; terminal: boolean; needsInfo: boolean };
  sla: { firstResponseDueAt: string | null; overdue: boolean };
  deposit: {
    held: boolean;
    status_code: number | null;
    host_insurance: boolean;
    locked_amount: number | null;
    locked_currency: string | null;
    hold: {
      amount: number | null;
      currency: string | null;
      stripe_ref: string | null;
      is_done: boolean | null;
      created_at: string | null;
    } | null;
  };
  inspection: {
    pre: AdminInspectionPhaseFacts | null;
    post: AdminInspectionPhaseFacts | null;
    pairs: AdminComparePair[];
    unslotted: { pre: number; post: number; damage: number };
  } | null;
  verification: {
    licence_status: number | null;
    licence_verified_at: string | null;
    licence_expiry: string | null;
    issuing_state: string | null;
    issuing_country: string | null;
    mvr_status: string | null;
    mvr_completed_at: string | null;
    us_verification_completed_at: string | null;
  } | null;
  valuation: { amount: number; source: "booking_snapshot" | "listing_current" } | null;
  thread: { public_id: string; conversation_id: number } | null;
  available_actions: AdminClaimAction[];
}

export type ClaimEmailParty = "host" | "renter";
export type ClaimEmailPurpose =
  | "case_update_host"
  | "evidence_request_host"
  | "case_update_renter"
  | "liability_update_renter"
  | "deposit_update_renter";

export interface AdminClaimCommunication {
  id: number;
  user_id: number | null;
  channel: string;
  purpose: string;
  recipient: string | null;
  state: "sent" | "suppressed" | "failed" | "skipped" | string;
  reason: string | null;
  provider_message_id: string | null;
  sent_at: string;
  snapshot: { subject: string; html: string } | null;
}

export interface AdminClaimEmailPreview {
  party: ClaimEmailParty;
  purpose: ClaimEmailPurpose;
  recipient: { email: string; user_id: string };
  preview_token: string;
  subject: string;
  html: string;
}

export function previewAdminClaimEmail(token: string, id: number, body: { party: ClaimEmailParty; purpose: ClaimEmailPurpose; note?: string }) {
  return adminFetch<AdminClaimEmailPreview>(`/admin/claims/${id}/communications/preview`, token, {
    method: "POST", body: JSON.stringify(body),
  });
}

export function sendAdminClaimEmail(token: string, id: number, body: { party: ClaimEmailParty; purpose: ClaimEmailPurpose; note?: string; preview_token?: string }) {
  return adminFetch<{ outcome: string; reason: string | null; recipient: { email: string; party: ClaimEmailParty } }>(`/admin/claims/${id}/communications/send`, token, {
    method: "POST", body: JSON.stringify(body),
  });
}

export interface AdminClaimPreview {
  allowed: boolean;
  to?: AdminClaimStatus;
  to_label?: string;
  step_up_required?: boolean;
  error?: string;
  consequences: string[];
}

// Task #20048 — per-party conversation streams + chat→evidence filing.
export interface AdminClaimThreadMessage {
  id: number;
  sender_role: "host" | "renter" | "support" | "system";
  content: string | null;
  attachment_url: string | null;
  attachment_type: string | null;
  /** Set when this attachment is already filed as claim evidence. */
  evidence_id: number | null;
  created_at: string;
}

export interface AdminClaimThread {
  conversation_id: number;
  public_id: string;
  messages: AdminClaimThreadMessage[];
}

export interface AdminClaimThreads {
  support_configured: boolean;
  threads: { host: AdminClaimThread | null; renter: AdminClaimThread | null };
}

export function getAdminClaimThreads(token: string, id: number) {
  return adminFetch<AdminClaimThreads>(`/admin/claims/${id}/threads`, token);
}

export function postAdminClaimThreadMessage(token: string, id: number, party: "host" | "renter", content: string) {
  return adminFetch<{ thread: { conversation_id: number; public_id: string }; message_id: number | null }>(
    `/admin/claims/${id}/threads/${party}/messages`,
    token,
    { method: "POST", body: JSON.stringify({ content }) },
  );
}

export function fileChatAttachmentAsEvidence(token: string, id: number, messageId: number, kind?: string, note?: string) {
  return adminFetch<{ evidence: AdminClaimEvidence }>(`/admin/claims/${id}/evidence/from-message`, token, {
    method: "POST",
    body: JSON.stringify({ message_id: messageId, kind, note }),
  });
}

// Task #20050 — AI damage pre-read (decision support; internal cost band).
export interface AdminClaimAiFinding {
  id: number;
  slot: string;
  change: "new_damage" | "pre_existing";
  panel: string | null;
  damage_type: string | null;
  severity: "minor" | "moderate" | "severe" | null;
  description: string | null;
  confidence: "low" | "medium" | "high";
  verdict: "ai_suggested" | "confirmed" | "dismissed";
  verdict_by: string | null;
  verdict_at: string | null;
}

export interface AdminClaimAi {
  assessment: {
    id: number;
    status: "running" | "complete" | "failed" | "skipped";
    model: string | null;
    integrity: Array<{ key: string; ok: boolean; detail: string }> | null;
    summary_draft: string | null;
    summary_accepted_by: string | null;
    summary_accepted_at: string | null;
    error: string | null;
    created_at: string;
  } | null;
  findings: AdminClaimAiFinding[];
  band: { low: number; high: number } | null;
  band_verdict: "within" | "above" | "below" | null;
  armed: boolean;
}

export function getAdminClaimAi(token: string, id: number) {
  return adminFetch<AdminClaimAi>(`/admin/claims/${id}/ai`, token);
}

export function rerunAdminClaimAi(token: string, id: number) {
  return adminFetch<{ queued: boolean }>(`/admin/claims/${id}/ai/rerun`, token, { method: "POST", body: "{}" });
}

export function setAdminClaimAiVerdict(token: string, id: number, findingId: number, verdict: "confirmed" | "dismissed") {
  return adminFetch<{ finding: { id: number; verdict: string } }>(`/admin/claims/${id}/ai/findings/${findingId}/verdict`, token, {
    method: "POST",
    body: JSON.stringify({ verdict }),
  });
}

export function setAdminClaimAiSummary(token: string, id: number, accept: boolean) {
  return adminFetch<{ accepted: boolean }>(`/admin/claims/${id}/ai/summary`, token, {
    method: "POST",
    body: JSON.stringify({ accept }),
  });
}

export function listAdminClaims(
  token: string,
  params: { status?: string; assignee?: string; q?: string; trip_id?: number; limit?: number; offset?: number } = {},
) {
  const sp = new URLSearchParams();
  if (params.status) sp.set("status", params.status);
  if (params.assignee) sp.set("assignee", params.assignee);
  if (params.q) sp.set("q", params.q);
  if (params.trip_id) sp.set("trip_id", String(params.trip_id));
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.offset) sp.set("offset", String(params.offset));
  const qs = sp.toString();
  return adminFetch<{ rows: AdminClaimRow[]; total: number; limit: number; offset: number }>(
    `/admin/claims${qs ? `?${qs}` : ""}`,
    token,
  );
}

export function getAdminClaim(token: string, id: number) {
  return adminFetch<AdminClaimDetail>(`/admin/claims/${id}`, token);
}

export function previewAdminClaimAction(token: string, id: number, action: AdminClaimAction) {
  return adminFetch<AdminClaimPreview>(`/admin/claims/${id}/preview?action=${action}`, token);
}

export function assignAdminClaim(token: string, id: number, assigneeId: number | "me" | null) {
  return adminFetch<{ claim: AdminClaimRow }>(`/admin/claims/${id}/assign`, token, {
    method: "POST",
    body: JSON.stringify({ assignee_id: assigneeId }),
  });
}

export function transitionAdminClaim(
  token: string,
  id: number,
  body: { action: AdminClaimAction; note?: string; insurer_ref?: string },
) {
  return adminFetch<{ claim: AdminClaimRow; consequences: string[] }>(`/admin/claims/${id}/transition`, token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function openAdminClaim(
  token: string,
  body: { trip_id: number; category?: string; description: string; amount_cents: number; source?: string },
) {
  return adminFetch<{ claim: AdminClaimRow }>(`/admin/claims`, token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Evidence on behalf: mint → PUT → bind (mirrors the host pipeline). */
export async function uploadAdminClaimEvidence(
  token: string,
  claimId: number,
  file: File,
  kind: string,
  source: "host" | "renter" | "admin",
  note?: string,
) {
  const contentType = file.type || "image/jpeg";
  const minted = await adminFetch<{ uploadURL: string; objectPath: string; uploadToken: string }>(
    `/admin/claims/${claimId}/evidence/request-upload-url`,
    token,
    { method: "POST", body: JSON.stringify({ contentType }) },
  );
  const putRes = await fetch(minted.uploadURL, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": contentType },
  });
  if (!putRes.ok) throw new Error(`Storage upload failed (${putRes.status})`);
  return adminFetch<{ evidence: AdminClaimEvidence }>(`/admin/claims/${claimId}/evidence`, token, {
    method: "POST",
    body: JSON.stringify({
      objectPath: minted.objectPath,
      uploadToken: minted.uploadToken,
      kind,
      mimeType: contentType,
      filename: file.name?.slice(0, 200),
      note,
      source,
    }),
  });
}
